import React, {ReactNode} from "react";
import {Dialog as MuiDialog} from "@mui/material";
import {useModal} from "@ebay/nice-modal-react";
import {Button, CloseDialogButton, Container, Text} from "../../../../ui";
import {isEmpty} from "lodash";
import {InputUtils} from "../../../../helpers";
import {useIsMobile} from "../../../helpers";

export type DialogProps = {
  title: string | ReactNode;
  hasCenteredTitle?: boolean;
  image?: ReactNode;
  onClick?: () => void | Promise<void>;
  onClose?: () => void;
  buttonText?: string;
  disabled?: boolean;
  autoClose?: boolean;
  width?: string;
  secondaryButtonText?: string;
  onClickSecondary?: () => void;
  isError?: boolean;
  children?: ReactNode;
};

export const Dialog = ({
  title,
  hasCenteredTitle,
  buttonText = "Confirm",
  image,
  onClick,
  onClose,
  secondaryButtonText,
  onClickSecondary,
  isError,
  disabled = false,
  width,
  autoClose = true,
  children,
}: DialogProps) => {
  const modal = useModal();
  const isMobile = useIsMobile();

  const onClickModal = async () => {
    onClick?.();
    if (autoClose) {
      modal.resolve(true);
      modal.hide();
    }
  };

  const onClickSecondaryModal = async () => {
    onClickSecondary?.();
    modal.hide();
  };

  return (
    <MuiDialog
      open={modal.visible}
      TransitionProps={{
        onExited: () => {
          modal.remove();
          onClose?.();
        },
      }}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
          padding: isMobile ? "44px 24px 24px 24px" : "44px",
          "&.MuiPaper-elevation": {
            margin: "12px",
          },
        },
      }}
    >
      <CloseDialogButton onClose={modal.hide} />
      <Container sx={{gap: "24px", width: width || "300px"}}>
        {image}
        {!isEmpty(title) && (
          <Container
            fullWidth
            sx={{
              alignItems: hasCenteredTitle ? "center" : "start",
              textAlign: hasCenteredTitle ? "center" : "left",
              whiteSpace: "pre-line",
            }}
          >
            <Text variant={"interDisplay600"} size={24} sx={{lineHeight: "32px"}}>
              {title}
            </Text>
          </Container>
        )}
        {children}
        <Container fullWidth sx={{gap: "8px"}}>
          <Button
            buttonType={isError ? "contained-error" : "contained-primary"}
            onClick={onClickModal}
            disabled={disabled}
            fullWidth
          >
            {buttonText}
          </Button>
          {secondaryButtonText && (
            <Button
              buttonType={isError ? "outlined-secondary" : "contained-secondary"}
              onClick={onClickSecondaryModal}
              fullWidth
            >
              {InputUtils.prettierSnakeCaseSentence(secondaryButtonText)}
            </Button>
          )}
        </Container>
      </Container>
    </MuiDialog>
  );
};
